import useSearch from "@hooks/useSearch";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import FormAdvanceSearch from "~/components/Search/FormAdvanceSearch";
import Layout from "~/components/Layout";
import Pagination from "~/components/Pagination";
import Results from "~/components/Search/Results";
import SearchFilter from "~/components/Search/SearchFilter";
import { getCategories, getSubCategoriesById } from "~/lib/db/category";
import { DEFAULT_PAGE_SIZE, FIELDS, SEARCH_TYPE } from "~/utils/constants";
import { getQueryParamAsArray } from "~/utils/utils";
import ErrorResults from "~/components/Search/Results/ErrorResults";
import { type CategoryType } from "./ricerca";
import { type InferGetStaticPropsType, type NextPage } from "next";
import {
  addCategoriesDocCount,
  filterUnecessaryParents,
  haveParentCategory,
} from "../components/Search/searchUtils";
import { type ElasticSearchBucket } from "~/utils/elasticSearchUtils";

const Ricerca: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = (
  props
) => {
  const router = useRouter();
  const {
    data: searchedPosts,
    totalResults,
    isLoading,
    error,
    setIgnoringError,
  } = useSearch("search");

  const [searchText, setSearchText] = useState("");
  const [fields, setFields] = useState([FIELDS.CONTENT]);
  const [searchType, setSearchType] = useState<SEARCH_TYPE>(SEARCH_TYPE.EXACT);
  const [selectedSources, setSelectedSources] = useState<CategoryType[]>([]);
  const [selectedSubSources, setSelectedSubSources] = useState<CategoryType[]>(
    []
  );

  useEffect(() => {
    if (router.query.searchType)
      setSearchType(router.query.searchType as SEARCH_TYPE);
  }, [router.query.searchType]);

  useEffect(() => {
    if (router.query.fields)
      setFields(getQueryParamAsArray(router.query.fields));
  }, [router.query.fields]);

  useEffect(() => {
    if (router.query.q) setSearchText(router.query.q as string);
  }, [router.query.q]);

  useEffect(() => {
    const urlSubSources =
      getQueryParamAsArray<string>(router.query.subsources) || [];
    const urlSources = getQueryParamAsArray<string>(router.query.sources) || [];

    const resultSubSources = urlSubSources.map((urlSubsource) =>
      props.subCategories.find((category) => category.value == urlSubsource)
    );

    const resultSources = props.categories.filter(
      (category) =>
        urlSources.includes(category.value) ||
        haveParentCategory(resultSubSources, category.id)
    );

    setSelectedSubSources(resultSubSources);
    setSelectedSources(resultSources);
  }, [router.query.subsources, router.query.sources]);

  const onFilter = () => {
    const routerQuery = router.query;
    routerQuery.q = searchText;
    routerQuery.fields = fields;
    routerQuery.searchType = searchType;

    delete routerQuery.page;

    routerQuery.sources = filterUnecessaryParents(
      selectedSources,
      selectedSubSources
    ).map((c) => c.value);

    routerQuery.subsources = selectedSubSources.map(
      (subSource) => subSource.value
    );

    router.push({
      ...router,
      query: routerQuery,
      hash: "risultati",
    });
  };

  const subCategoriesOptions = addCategoriesDocCount(
    props.subCategories,
    searchedPosts?.aggregations?.category?.buckets as ElasticSearchBucket,
    searchedPosts?.aggregations?.multimedia?.buckets as ElasticSearchBucket,
    searchedPosts?.aggregations?.speciali.buckets as ElasticSearchBucket
  );

  const categoriesOptions = addCategoriesDocCount(
    props.categories,
    searchedPosts?.aggregations?.category?.buckets as ElasticSearchBucket,
    searchedPosts?.aggregations?.multimedia?.buckets as ElasticSearchBucket,
    searchedPosts?.aggregations?.speciali.buckets as ElasticSearchBucket
  );

  return (
    <>
      <Layout title="Ricerca Avanzata">
        <section className="-mt-12 w-full">
          <div className="mx-auto max-w-box px-4 py-20 md:pb-32 md:pt-24">
            <div className="mb-20 lg:mb-32">
              <h1 className="text-4xl font-light md:text-6xl">Ricerca</h1>
            </div>
            <FormAdvanceSearch
              searchText={searchText}
              setSearchText={setSearchText}
              fields={fields}
              setFields={setFields}
              searchType={searchType}
              setSearchType={setSearchType}
              loading={isLoading}
            />
          </div>
          {(searchedPosts || error || isLoading) && (
            <div className="mx-auto w-full rounded-lg">
              {searchedPosts && (
                <>
                  <h3 className="mx-auto max-w-box px-4 pt-4 text-xl font-extrabold sm:text-2xl md:mb-12 md:pb-6 lg:pb-8">
                    Abbiamo trovato
                  </h3>
                  <div className="mx-auto mb-12 max-w-box px-4">
                    <SearchFilter
                      subCategories={subCategoriesOptions}
                      categories={categoriesOptions}
                      selectedSources={selectedSources}
                      selectedSubSources={selectedSubSources}
                      setSelectedSources={setSelectedSources}
                      setSelectedSubSources={setSelectedSubSources}
                      onFilter={onFilter}
                    />
                  </div>
                </>
              )}
              <div className="mx-auto mb-12 max-w-box">
                <div className="mx-auto max-w-box px-5 pt-4">
                  {error && (
                    <ErrorResults
                      error={error}
                      onErrorDismiss={setIgnoringError}
                    />
                  )}
                  {(searchedPosts || isLoading) && (
                    <>
                      <Results
                        posts={searchedPosts?.hits?.hits}
                        totalResults={totalResults}
                        loading={isLoading}
                      />
                      {totalResults > 0 && (
                        <Pagination
                          totalResults={totalResults}
                          itemsPerPage={DEFAULT_PAGE_SIZE}
                          preString="Risultati precedenti"
                          nextString="Risultati successivi"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </Layout>
    </>
  );
};

export default Ricerca;

export const getStaticProps = async () => {
  const dbCategories = await getCategories();
  const categoriesId = dbCategories.map((el) => el.term_id);
  const dbSubCategories = await getSubCategoriesById(categoriesId);

  const categories: CategoryType[] = dbCategories.map((category) => ({
    id: category.term_id.toString(),
    value: category.slug,
    label: category.name.replace("&amp;", "&"),
    totArticles: 0,
  }));

  const subCategories: CategoryType[] = dbSubCategories.map((category) => ({
    parentId: category.parentId.toString(),
    value: category.slug,
    label: category.name.replace("&amp;", "&"),
    totArticles: 0,
  }));

  return {
    props: {
      categories,
      subCategories,
    },
  };
};
